import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { Wallet } from "@/types";
import { getIntegrationColorClass } from "./kanbanConfig";

interface KanbanCardProps {
	wallet: Wallet;
	isDragOverlay?: boolean;
}

export function KanbanCard({ wallet, isDragOverlay }: KanbanCardProps) {
	const navigate = useNavigate();
	const { attributes, listeners, setNodeRef, transform, isDragging } =
		useDraggable({ id: wallet.id });

	const style = transform
		? { transform: CSS.Translate.toString(transform) }
		: undefined;

	return (
		<Card
			ref={setNodeRef}
			style={style}
			{...listeners}
			{...attributes}
			className={cn(
				"cursor-grab px-3 py-2.5 transition-all duration-150 hover:border-(--color-primary) hover:shadow-md",
				isDragging && "opacity-30",
				isDragOverlay && "rotate-2 shadow-lg",
			)}
			role="button"
			tabIndex={0}
			onClick={() => {
				if (!isDragging) navigate(`/wallet/${wallet.id}`);
			}}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					navigate(`/wallet/${wallet.id}`);
				}
			}}
		>
			<div className="flex items-center gap-2">
				<span
					className={cn(
						"h-2 w-2 shrink-0 rounded-full",
						getIntegrationColorClass(wallet.integration_status),
					)}
				/>
				<span className="min-w-0 truncate font-medium text-(--color-foreground) text-sm leading-tight">
					{wallet.wallet_name}
				</span>
				{wallet.customer_success?.tier && (
					<Badge
						variant={
							wallet.customer_success.tier === 1
								? "success"
								: wallet.customer_success.tier === 2
									? "info"
									: "warning"
						}
						className="ml-auto shrink-0 px-1.5 py-0 text-[0.5rem]"
					>
						T{wallet.customer_success.tier}
					</Badge>
				)}
			</div>
		</Card>
	);
}
